import AsyncStorage from '@react-native-async-storage/async-storage';
let _profUserId = 'default';
try {
  require('@react-native-async-storage/async-storage').default
    .getItem('userToken').then(t => { if (t) _profUserId = t.slice(0,16); }).catch(() => {});
} catch {}

/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  SafeAreaView, View, Text, TextInput, StyleSheet, FlatList,
  RefreshControl, TouchableOpacity, Switch, ActivityIndicator,
  ScrollView, Dimensions, Image, Alert, Linking, Platform,
  Modal, StatusBar, Share, Animated, KeyboardAvoidingView,
} from 'react-native';
import * as Location from 'expo-location';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';

// ─── External job search service ─────────────────────────────────────────────
import {
  searchAllJobs, searchJobsByType,
} from '../../services/unifiedJobAPI';

// ─── Contexts ─────────────────────────────────────────────────────────────────
import { useProfessional } from '../../context/ProfessionalContext';
import { useGeneral }      from '../../context/GeneralContext';
import { apiFetch } from '../../utils/apiClient';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import useProfile from '../../hooks/useProfile';

// ─── Internal API ────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const { width } = Dimensions.get('window');

// ─── Constants ────────────────────────────────────────────────────────────────
const JOB_TYPES       = ['All', 'Full-time', 'Part-time', 'Contract', 'Freelance', 'Remote', 'Hybrid', 'Internship'];
const EXP_LEVELS      = ['All Levels', 'Entry Level', 'Mid Level', 'Senior', 'Lead', 'Director'];
const SALARY_RANGES   = [
  { label:'Any',       min:0,      max:999999 },
  { label:'£20k–£35k', min:20000,  max:35000  },
  { label:'£35k–£50k', min:35000,  max:50000  },
  { label:'£50k–£70k', min:50000,  max:70000  },
  { label:'£70k–£90k', min:70000,  max:90000  },
  { label:'£90k–£120k',min:90000,  max:120000 },
  { label:'£120k+',    min:120000, max:999999 },
];
const DATE_POSTED     = ['Any Time', 'Last 24h', 'Last 3 days', 'Last week', 'Last month'];
const SORT_OPTIONS    = ['Best Match', 'Newest First', 'Highest Salary', 'Most Relevant', 'Closest'];
const RADIUS_OPTIONS  = [5, 10, 25, 50, 100];
const ALERT_FREQS     = ['Instantly', 'Daily', 'Weekly'];

const ENG_CATEGORIES = [
  { id:'software',   label:'Software',    icon:'code-slash-outline',  color:'#3B82F6' },
  { id:'mechanical', label:'Mechanical',  icon:'construct-outline',   color:'#F59E0B' },
  { id:'electrical', label:'Electrical',  icon:'flash-outline',       color:'#EF4444' },
  { id:'civil',      label:'Civil',       icon:'business-outline',    color:'#10B981' },
  { id:'data',       label:'Data & AI',   icon:'analytics-outline',   color:'#8B5CF6' },
  { id:'aerospace',  label:'Aerospace',   icon:'airplane-outline',    color:'#06B6D4' },
  { id:'chemical',   label:'Chemical',    icon:'flask-outline',       color:'#F97316' },
  { id:'robotics',   label:'Robotics',    icon:'hardware-chip-outline',color:'#EC4899' },
  { id:'management', label:'Management',  icon:'people-outline',      color:'#6B7280' },
];

const TRENDING_SEARCHES = [
  'React Native developer', 'Python engineer', 'Civil engineer London',
  'DevOps remote', 'Data scientist', 'Mechanical engineer CAD',
  'Full-stack developer', 'Electrical engineer', 'Project manager',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtSalary = (min, max, cur = '£') => {
  if (min && max) return `${cur}${(+min/1000).toFixed(0)}k–${cur}${(+max/1000).toFixed(0)}k`;
  if (min) return `From ${cur}${(+min/1000).toFixed(0)}k`;
  return 'Competitive';
};
const parseSalNum = (job) => {
  const v = job.salary_min || job.salaryMin || job.min_salary;
  if (v) return +v;
  if (job.salary) { const m = job.salary.replace(/,/g,'').match(/\d+/); return m?+m[0]:0; }
  return 0;
};
const fmtAgo = (d) => {
  if (!d) return 'Recently';
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return '1d ago';
  if (days < 7)   return `${days}d ago`;
  if (days < 30)  return `${Math.floor(days/7)}w ago`;
  return `${Math.floor(days/30)}mo ago`;
};
const matchColor = (s) => s >= 75 ? '#10B981' : s >= 50 ? '#F59E0B' : '#9CA3AF';
const typeColor  = (t = '') => {
  if (t.toLowerCase().includes('full'))      return { bg:'#DCFCE7', text:'#16A34A' };
  if (t.toLowerCase().includes('part'))      return { bg:'#FEF3C7', text:'#D97706' };
  if (t.toLowerCase().includes('contract')||t.toLowerCase().includes('freelance')) return { bg:'#FEE2E2', text:'#DC2626' };
  if (t.toLowerCase().includes('remote'))    return { bg:'#DBEAFE', text:'#2563EB' };
  if (t.toLowerCase().includes('hybrid'))    return { bg:'#F3E8FF', text:'#7C3AED' };
  if (t.toLowerCase().includes('intern'))    return { bg:'#ECFDF5', text:'#059669' };
  return { bg:'#F1F5F9', text:'#64748B' };
};

// Normalises skills (job OR user) into a clean array of strings.
// Sources vary wildly: ["React"], [{name:"React"}], [{skill_name:"React"}],
// external Reed jobs, and stale caches — coerce every shape so .toLowerCase() can't crash.
const toSkillStrings = (v) => {
  let raw = v ?? [];
  if (typeof raw === 'string') raw = raw.split(',');
  if (!Array.isArray(raw)) return [];
  return raw
    .map(s => (typeof s === 'string' ? s : (s?.name || s?.title || s?.skill_name || s?.skill || '')))
    .map(s => String(s).trim())
    .filter(Boolean);
};

const transformApiJob = (job) => ({
  id:          String(job.id || job.jobId || Math.random()),
  jobId:       job.jobId || job.id,
  title:       job.title || 'Job Title',
  company:     job.companyName || job.company || job.department || 'Company',
  companyLogo: job.logo || job.clientLogo || null,
  location:    job.location || job.locationName || 'UK',
  type:        job.jobType || job.job_type || job.type || 'Full-time',
  salary:      job.salary || fmtSalary(job.salary_min||job.salaryMin, job.salary_max||job.salaryMax),
  salaryMin:   job.salary_min || job.salaryMin || 0,
  applyLink:   job.applyLink || job.apply_link || null,
  isRemote:    job.isRemote || job.is_remote || false,
  isUrgent:    job.isUrgent || job.is_urgent || false,
  posted:      job.posted || job.created_at || job.createdAt || null,
  description: job.description || '',
  requirements:job.requirements || job.qualifications || [],
  benefits:    job.benefits || [],
  skills:      toSkillStrings(job.skills || job.required_skills || job.requiredSkills),
  matchScore:  job.matchScore || job.ai_score || null,
  dataSource:  job.dataSource || 'jobeng',
  experience:  job.experience_level || job.experienceLevel || job.experience || '',
  applicants:  job.applicants || job.application_count || null,
  deadline:    job.deadline || job.closing_date || null,
  views:       job.views || null,
});

// ─── Match score calculator ───────────────────────────────────────────────────
const calcMatch = (job, userSkills = []) => {
  if (!userSkills.length) return null;
  const jSkills = toSkillStrings(job.skills).map(s => s.toLowerCase());
  if (!jSkills.length) return null;
  const matched = userSkills.filter(us => jSkills.some(js => js.includes(String(us).toLowerCase())));
  return Math.round((matched.length / jSkills.length) * 100);
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SearchJobsScreen() {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const navigation = useNavigation();

  // Contexts
  const {
    savedJobs = [], jobApplications = [],
    saveJob, unsaveJob, applyToJob,
  } = useProfessional() || {};
  const { showToast } = useGeneral() || {};

  // Auth / identity
  const [userId,     setUserId]     = useState('default');
  const [userSkills, setUserSkills] = useState([]);

  // Search state
  const [query,         setQuery]         = useState('');
  const [locationQuery, setLocationQuery] = useState('United Kingdom');
  const [useNearby,     setUseNearby]     = useState(false);
  const [gpsCoords,     setGpsCoords]     = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [radius,        setRadius]        = useState(25);

  // Filters
  const [selType,     setSelType]     = useState('All');
  const [selExp,      setSelExp]      = useState('All Levels');
  const [selSalary,   setSelSalary]   = useState(0);
  const [selDate,     setSelDate]     = useState('Any Time');
  const [selSort,     setSelSort]     = useState('Best Match');
  const [remoteOnly,  setRemoteOnly]  = useState(false);
  const [urgentOnly,  setUrgentOnly]  = useState(false);
  const [activeFilterCount, setActiveFilterCount] = useState(0);

  // Results
  const [results,      setResults]      = useState([]);
  const [totalCount,   setTotalCount]   = useState(0);
  const [page,         setPage]         = useState(1);
  const [hasMore,      setHasMore]      = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [loadingMore,  setLoadingMore]  = useState(false);
  const [searched,     setSearched]     = useState(false);
  const [error,        setError]        = useState(null);

  // Saved / applied sets
  const [savedIds,   setSavedIds]   = useState(new Set());
  const [appliedIds, setAppliedIds] = useState(new Set());

  // History & suggestions
  const [recentSearches,   setRecentSearches]   = useState([]);
  const [suggestedJobs,    setSuggestedJobs]     = useState([]);
  const [showSuggestions,  setShowSuggestions]  = useState(false);

  // Modals
  const [filterModal,  setFilterModal]  = useState(false);
  const [detailModal,  setDetailModal]  = useState(false);
  const [alertModal,   setAlertModal]   = useState(false);
  const [compareModal, setCompareModal] = useState(false);
  const [selectedJob,  setSelectedJob]  = useState(null);
  const [compareJobs,  setCompareJobs]  = useState([]);
  const [compareMode,  setCompareMode]  = useState(false);

  // Alert form
  const [alertForm, setAlertForm] = useState({ title:'', frequency:'Daily', active:true });
  const [savingAlert, setSavingAlert] = useState(false);

  // View mode
  const [viewMode, setViewMode] = useState('list'); // list | grid

  // Category browsing
  const [selCategory, setSelCategory] = useState(null);

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const queryRef = useRef(null);

  // ─── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    bootstrap();
  }, []);

  useEffect(() => {
    recountFilters();
  }, [selType, selExp, selSalary, selDate, remoteOnly, urgentOnly, selCategory]);

  const bootstrap = async () => {
    const uid = await AsyncStorage.getItem('userId') || await AsyncStorage.getItem('user_id');
    setUserId(uid || 'default');
    await Promise.all([
      loadRecentSearches(),
      loadUserSkills(uid),
      loadSavedIds(uid),
      loadAppliedIds(uid),
      loadSuggestedJobs(),
    ]);
    Animated.timing(fadeAnim, { toValue:1, duration:400, useNativeDriver:true }).start();
  };

  const loadUserSkills = async (uid) => {
    const cached = await AsyncStorage.getItem(`user_skills_${uid||'g'}`);
    if (cached) { setUserSkills(toSkillStrings(JSON.parse(cached))); return; }
    try {
      const profRaw = await AsyncStorage.getItem('prof_user_profile') || await AsyncStorage.getItem('userProfile');
      if (profRaw) {
        const prof = JSON.parse(profRaw);
        const sk = toSkillStrings(prof?.skills);
        setUserSkills(sk);
        return;
      }
      if (uid) {
        const r = await apiFetch(`/api/users/${uid}/skills`);
        if (!r.error && r.data) {
          const sk = toSkillStrings(r.data);
          setUserSkills(sk);
          await AsyncStorage.setItem(`user_skills_${uid}`, JSON.stringify(sk));
        }
      }
    } catch {}
  };

  const loadSavedIds = async (uid) => {
    try {
      const cached = await AsyncStorage.getItem(`saved_job_ids_${uid||'g'}`);
      if (cached) setSavedIds(new Set(JSON.parse(cached)));
      const ids = (Array.isArray(savedJobs) ? savedJobs : []).map(j=>String(j.id||j.jobId));
      if (ids.length) setSavedIds(new Set(ids));
    } catch {}
  };

  const loadAppliedIds = async (uid) => {
    try {
      const cached = await AsyncStorage.getItem(`applied_job_ids_${uid||'g'}`);
      if (cached) setAppliedIds(new Set(JSON.parse(cached)));
      const ids = (Array.isArray(jobApplications)?jobApplications:[]).map(a=>String(a.jobId||a.id));
      if (ids.length) setAppliedIds(new Set(ids));
    } catch {}
  };

  const loadRecentSearches = async () => {
    const raw = await AsyncStorage.getItem(`search_history_${_profUserId}`);
    if (raw) setRecentSearches(JSON.parse(raw).slice(0,8));
  };

  const loadSuggestedJobs = async () => {
    try {
      const cached = await AsyncStorage.getItem(`suggested_jobs_${_profUserId}`);
      if (cached) { setSuggestedJobs((JSON.parse(cached) || []).map(transformApiJob)); return; }
      const r = await apiFetch('/api/jobs?status=active&limit=10&sort=created_at&order=desc');
      if (!r.error) {
        const jobs = (r.data||r.jobs||Array.isArray(r)?r:[]).map(transformApiJob);
        setSuggestedJobs(jobs);
        await AsyncStorage.setItem(`suggested_jobs_${_profUserId}`, JSON.stringify(jobs));
      }
    } catch {}
  };

  // ─── GPS location ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (useNearby) {
      (async () => {
        setLocationError(null);
        const { status } = await Location.requestForegroundPermissionsAsync();
        if (status !== 'granted') {
          setLocationError('Location permission denied');
          setUseNearby(false); return;
        }
        try {
          const loc = await Location.getCurrentPositionAsync({});
          setGpsCoords(loc.coords);
          const geo = await Location.reverseGeocodeAsync(loc.coords);
          if (geo?.[0]) {
            const city = geo[0].city || geo[0].region || 'United Kingdom';
            setLocationQuery(city);
          }
        } catch { setLocationError('Could not get location'); setUseNearby(false); }
      })();
    } else {
      setGpsCoords(null);
      if (!locationQuery || locationQuery === '') setLocationQuery('United Kingdom');
    }
  }, [useNearby]);

  // ─── Filter count ──────────────────────────────────────────────────────────
  const recountFilters = () => {
    let c = 0;
    if (selType !== 'All') c++;
    if (selExp  !== 'All Levels') c++;
    if (selSalary !== 0) c++;
    if (selDate !== 'Any Time') c++;
    if (remoteOnly) c++;
    if (urgentOnly) c++;
    if (selCategory) c++;
    setActiveFilterCount(c);
  };

  const resetFilters = () => {
    setSelType('All'); setSelExp('All Levels'); setSelSalary(0);
    setSelDate('Any Time'); setSelSort('Best Match');
    setRemoteOnly(false); setUrgentOnly(false); setSelCategory(null);
  };

  // ─── Search ───────────────────────────────────────────────────────────────
  const saveSearch = async (q) => {
    if (!q.trim()) return;
    const updated = [q, ...recentSearches.filter(r=>r!==q)].slice(0,8);
    setRecentSearches(updated);
    await AsyncStorage.setItem(`search_history_${_profUserId}`, JSON.stringify(updated));
  };

  const handleSearch = async (overrideQuery = null, pageNum = 1) => {
    const q = (overrideQuery ?? query).trim() || 'engineering jobs UK';
    if (pageNum === 1) { setLoading(true); setResults([]); setError(null); }
    else setLoadingMore(true);

    setSearched(true);
    setShowSuggestions(false);
    if (pageNum === 1) await saveSearch(q);

    try {
      // ── 1. Internal JobEng API ─────────────────────────────────────────────
      let internalJobs = [];
      const params = new URLSearchParams({
        status: 'active', limit: '20',
        ...(q !== 'engineering jobs UK' ? { q } : {}),
        ...(locationQuery && locationQuery !== 'United Kingdom' ? { location: locationQuery } : {}),
        ...(selType !== 'All' ? { job_type: selType } : {}),
        ...(selExp !== 'All Levels' ? { experience_level: selExp } : {}),
        ...(selSalary !== 0 ? { salary_min: SALARY_RANGES[selSalary].min, salary_max: SALARY_RANGES[selSalary].max } : {}),
        page: pageNum,
      });

      const internalRes = await apiFetch(`/api/jobs?${params.toString()}`);
      if (!internalRes.error) {
        const raw = internalRes.data || internalRes.jobs || (Array.isArray(internalRes) ? internalRes : []);
        internalJobs = raw.map(transformApiJob);
        if (pageNum === 1) setTotalCount(internalRes.total || internalRes.count || raw.length);
      }

      // ── 2. External unified job API ────────────────────────────────────────
      let externalJobs = [];
      try {
        const typeMap = { 'Full-time':'fulltime','Part-time':'parttime','Contract':'contract','Freelance':'contract','Remote':'remote' };
        const extFn = selType !== 'All' ? searchJobsByType(typeMap[selType]||'fulltime',{ query:q, location:locationQuery, page:pageNum }) : searchAllJobs({ query:q, location:locationQuery, page:pageNum });
        const extRes = await extFn;
        if (extRes?.success && extRes.jobs) externalJobs = extRes.jobs.map(transformApiJob);
      } catch {}

      // ── 3. Merge & deduplicate ─────────────────────────────────────────────
      let all = [...internalJobs];
      const internalIds = new Set(internalJobs.map(j=>j.title?.toLowerCase()+j.company?.toLowerCase()));
      externalJobs.forEach(j => {
        const key = (j.title||'').toLowerCase()+(j.company||'').toLowerCase();
        if (!internalIds.has(key)) all.push(j);
      });

      // ── 4. Client-side filters ─────────────────────────────────────────────
      if (remoteOnly) all = all.filter(j => j.isRemote || j.type?.toLowerCase().includes('remote') || j.location?.toLowerCase().includes('remote'));
      if (urgentOnly) all = all.filter(j => j.isUrgent);
      if (selDate !== 'Any Time') {
        const msMap = { 'Last 24h':86400000,'Last 3 days':3*86400000,'Last week':7*86400000,'Last month':30*86400000 };
        const ms = msMap[selDate];
        if (ms) all = all.filter(j => j.posted && (Date.now()-new Date(j.posted).getTime()) < ms);
      }

      // ── 5. Enrich with match score ─────────────────────────────────────────
      all = all.map(j => ({
        ...j,
        matchScore: j.matchScore ?? calcMatch(j, userSkills),
      }));

      // ── 6. Sort ────────────────────────────────────────────────────────────
      switch (selSort) {
        case 'Newest First':    all.sort((a,b) => new Date(b.posted||0) - new Date(a.posted||0)); break;
        case 'Highest Salary':  all.sort((a,b) => parseSalNum(b) - parseSalNum(a)); break;
        case 'Best Match':      all.sort((a,b) => (b.matchScore||0) - (a.matchScore||0)); break;
        case 'Most Relevant':   break; // API order
      }

      // ── 7. Set state ───────────────────────────────────────────────────────
      if (pageNum === 1) setResults(all);
      else setResults(prev => [...prev, ...all]);
      setHasMore(all.length >= 20);
      setPage(pageNum);

      // Cache results
      if (pageNum === 1) await AsyncStorage.setItem(`search_cache_${_profUserId}`, JSON.stringify({ q, results:all.slice(0,20), ts:Date.now() }));

    } catch (e) {
      setError('Search failed. Please try again.');
      // Load cache on error
      if (pageNum === 1) {
        const cached = await AsyncStorage.getItem(`search_cache_${_profUserId}`);
        if (cached) {
          const { results: cachedResults } = JSON.parse(cached);
          setResults((cachedResults || []).map(transformApiJob));
        }
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (!loadingMore && hasMore && !loading) handleSearch(null, page + 1);
  };

  // ─── Save / Apply ──────────────────────────────────────────────────────────
  const toggleSave = async (job) => {
    const id = String(job.id || job.jobId);
    const newSet = new Set(savedIds);
    try {
      if (newSet.has(id)) {
        newSet.delete(id);
        await unsaveJob?.(id);
        await apiFetch(`/api/professional/saved-jobs/${id}`, { method:'DELETE' });
        showToast?.('Removed from saved', 'info');
      } else {
        newSet.add(id);
        await saveJob?.(job);
        await apiFetch('/api/professional/saved-jobs', { method:'POST', body: JSON.stringify({ job_id: id, user_id: userId }) });
        showToast?.('Job saved!', 'success');
      }
      setSavedIds(newSet);
      await AsyncStorage.setItem(`saved_job_ids_${userId||'g'}`, JSON.stringify([...newSet]));
    } catch {}
  };

  const handleApply = async (job) => {
    const id = String(job.id||job.jobId);
    if (appliedIds.has(id)) { Alert.alert('Already Applied', 'You have already applied to this job.'); return; }
    if (job.applyLink) {
      Alert.alert('Apply via External Site', 'This will open the company application page.',
        [{ text:'Cancel', style:'cancel' },
         { text:'Open & Apply', onPress: async () => {
           await Linking.openURL(job.applyLink);
           await applyToJob?.(id, { appliedAt: new Date().toISOString(), method:'external' });
           const newSet = new Set(appliedIds); newSet.add(id);
           setAppliedIds(newSet);
           await AsyncStorage.setItem(`applied_job_ids_${userId||'g'}`, JSON.stringify([...newSet]));
           showToast?.('Application tracked', 'success');
         }}]
      );
    } else {
      setDetailModal(false);
      navigation.navigate('SubmitForJobs', { job, jobId: id });
    }
  };

  const handleShare = async (job) => {
    try {
      await Share.share({
        title: job.title,
        message: `${job.title} at ${job.company}\n${job.location} · ${job.salary || 'Competitive'}\n\nVia JobEng app`,
      });
    } catch {}
  };

  const openDetail = (job) => { setSelectedJob(job); setDetailModal(true); };

  const toggleCompare = (job) => {
    const id = String(job.id);
    const inList = compareJobs.find(j=>String(j.id)===id);
    if (inList) { setCompareJobs(p=>p.filter(j=>String(j.id)!==id)); return; }
    if (compareJobs.length >= 2) { Alert.alert('Limit','Select up to 2 jobs to compare.'); return; }
    setCompareJobs(p=>[...p,job]);
  };

  const createAlert = async () => {
    if (!alertForm.title.trim() && !query.trim()) { Alert.alert('Required','Enter a keyword for the alert.'); return; }
    setSavingAlert(true);
    try {
      const body = { user_id: userId, keywords: alertForm.title||query, location: locationQuery, job_type: selType, frequency: alertForm.frequency, active: true };
      await apiFetch('/api/professional/alerts', { method:'POST', body: JSON.stringify(body) });
      setAlertModal(false);
      Alert.alert('✅ Alert Created', `You'll be notified ${String(alertForm.frequency ?? '').toLowerCase()} when new matching jobs are posted.`);
    } catch { Alert.alert('Error','Could not create alert.'); }
    finally { setSavingAlert(false); }
  };

  // ─── Render: Header ────────────────────────────────────────────────────────
  const renderHeader = () => (
    <View style={s.header}>
      <TouchableOpacity onPress={() => navigation.goBack()} style={s.headerBack}>
        <Ionicons name="chevron-back" size={24} color="#FFF" />
      </TouchableOpacity>
      <View style={s.headerCenter}>
        <MaterialCommunityIcons name="briefcase-search" size={22} color="#FFF" />
        <Text style={s.headerTitle}>Search Jobs</Text>
      </View>
      <View style={s.headerRight}>
        <TouchableOpacity style={s.headerIcon} onPress={() => setViewMode(v=>v==='list'?'grid':'list')}>
          <MaterialCommunityIcons name={viewMode==='list'?'view-grid':'format-list-bulleted'} size={20} color="#FFF" />
        </TouchableOpacity>
        <TouchableOpacity style={s.headerIcon} onPress={() => setAlertModal(true)}>
          <MaterialCommunityIcons name="bell-plus-outline" size={20} color="#FFF" />
        </TouchableOpacity>
      </View>
    </View>
  );

  // ─── Render: Search bar ────────────────────────────────────────────────────
  const renderSearchBar = () => (
    <View style={s.searchSection}>
      {/* Title / location row */}
      <View style={s.searchRow}>
        <View style={s.searchBox}>
          <Ionicons name="search" size={18} color="#94a3b8" style={{ marginLeft:12 }} />
          <TextInput
            ref={queryRef}
            style={s.searchInput}
            placeholder="Job title, skills, company…"
            placeholderTextColor="#94a3b8"
            value={query}
            onChangeText={v => { setQuery(v); setShowSuggestions(v.length > 0); }}
            returnKeyType="search"
            onSubmitEditing={() => handleSearch()}
            onFocus={() => setShowSuggestions(query.length > 0 || recentSearches.length > 0)}
          />
          {query ? (
            <TouchableOpacity onPress={()=>{setQuery('');setShowSuggestions(false);}} style={{padding:10}}>
              <Ionicons name="close-circle" size={16} color="#94a3b8" />
            </TouchableOpacity>
          ) : null}
        </View>
        <TouchableOpacity
          style={[s.filterBtn, activeFilterCount>0 && s.filterBtnActive]}
          onPress={() => setFilterModal(true)}
        >
          <Ionicons name="options-outline" size={20} color={activeFilterCount>0?'#FFF':'#0e4e96'} />
          {activeFilterCount > 0 && <View style={s.filterBadge}><Text style={s.filterBadgeText}>{activeFilterCount}</Text></View>}
        </TouchableOpacity>
      </View>

      {/* Location row */}
      <View style={s.locationRow}>
        <View style={[s.searchBox, { flex:1 }]}>
          <Ionicons name="location-outline" size={16} color="#94a3b8" style={{ marginLeft:12 }} />
          <TextInput
            style={s.searchInput}
            placeholder="Location (e.g. London, UK)"
            placeholderTextColor="#94a3b8"
            value={locationQuery}
            onChangeText={setLocationQuery}
            editable={!useNearby}
          />
        </View>
        <View style={s.nearbyToggle}>
          <MaterialCommunityIcons name="crosshairs-gps" size={16} color={useNearby?'#0e4e96':'#94a3b8'} />
          <Switch
            value={useNearby}
            onValueChange={setUseNearby}
            trackColor={{ false:'#E5E7EB', true:'#BFDBFE' }}
            thumbColor={useNearby?'#0e4e96':'#D1D5DB'}
            style={{ transform:[{scaleX:0.8},{scaleY:0.8}] }}
          />
        </View>
      </View>

      {/* GPS status */}
      {useNearby && (
        <View style={s.gpsStatus}>
          {locationError
            ? <><Ionicons name="warning" size={14} color="#EF4444" /><Text style={[s.gpsText,{color:'#EF4444'}]}>{locationError}</Text></>
            : gpsCoords
            ? <><Ionicons name="checkmark-circle" size={14} color="#10B981" /><Text style={[s.gpsText,{color:'#10B981'}]}>Searching near {locationQuery} · {radius}mi radius</Text></>
            : <><ActivityIndicator size={12} color="#0e4e96" /><Text style={s.gpsText}>Getting location…</Text></>
          }
          {gpsCoords && (
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {RADIUS_OPTIONS.map(r => (
                <TouchableOpacity key={r} style={[s.radiusChip, radius===r && s.radiusChipActive]} onPress={()=>setRadius(r)}>
                  <Text style={[s.radiusText, radius===r && s.radiusTextActive]}>{r}mi</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          )}
        </View>
      )}

      {/* Active filter chips */}
      {activeFilterCount > 0 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.activeFilters}>
          {selType !== 'All'         && <FilterChip label={selType}       onRemove={()=>setSelType('All')} />}
          {selExp  !== 'All Levels'  && <FilterChip label={selExp}        onRemove={()=>setSelExp('All Levels')} />}
          {selSalary !== 0           && <FilterChip label={SALARY_RANGES[selSalary].label} onRemove={()=>setSelSalary(0)} />}
          {selDate !== 'Any Time'    && <FilterChip label={selDate}       onRemove={()=>setSelDate('Any Time')} />}
          {remoteOnly                && <FilterChip label="Remote Only"   onRemove={()=>setRemoteOnly(false)} />}
          {urgentOnly                && <FilterChip label="Urgent"        onRemove={()=>setUrgentOnly(false)} />}
          {selCategory               && <FilterChip label={selCategory}   onRemove={()=>setSelCategory(null)} />}
          <TouchableOpacity onPress={resetFilters} style={s.clearAll}>
            <Text style={s.clearAllText}>Clear all</Text>
          </TouchableOpacity>
        </ScrollView>
      )}

      {/* Search button */}
      <TouchableOpacity style={s.searchBtn} onPress={() => handleSearch()} disabled={loading}>
        {loading
          ? <ActivityIndicator size={18} color="#FFF" />
          : <><Ionicons name="search" size={16} color="#FFF" /><Text style={s.searchBtnText}>Search Jobs</Text></>
        }
      </TouchableOpacity>
    </View>
  );

  // ─── Render: Suggestions dropdown ─────────────────────────────────────────
  const renderSuggestions = () => showSuggestions && (
    <View style={s.suggestionsBox}>
      {query.length > 0 && TRENDING_SEARCHES.filter(t=>t.toLowerCase().includes(query.toLowerCase())).slice(0,4).map((t,i) => (
        <TouchableOpacity key={i} style={s.suggRow} onPress={() => { setQuery(t); setShowSuggestions(false); handleSearch(t); }}>
          <Ionicons name="search-outline" size={14} color="#94a3b8" />
          <Text style={s.suggText}>{t}</Text>
        </TouchableOpacity>
      ))}
      {recentSearches.length > 0 && (
        <>
          <Text style={s.suggSection}>Recent</Text>
          {recentSearches.slice(0,4).map((r,i) => (
            <TouchableOpacity key={i} style={s.suggRow} onPress={() => { setQuery(r); setShowSuggestions(false); handleSearch(r); }}>
              <Ionicons name="time-outline" size={14} color="#94a3b8" />
              <Text style={s.suggText}>{r}</Text>
              <TouchableOpacity onPress={async()=>{
                const upd = recentSearches.filter(x=>x!==r);
                setRecentSearches(upd);
                await AsyncStorage.setItem(`search_history_${_profUserId}`, JSON.stringify(upd));
              }}>
                <Ionicons name="close" size={14} color="#CBD5E1" />
              </TouchableOpacity>
            </TouchableOpacity>
          ))}
        </>
      )}
    </View>
  );

  // ─── Render: Category chips ────────────────────────────────────────────────
  const renderCategories = () => !searched && (
    <View style={s.catSection}>
      <Text style={s.catTitle}>Browse by Category</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal:16, gap:8 }}>
        {ENG_CATEGORIES.map(cat => (
          <TouchableOpacity key={cat.id} style={[s.catChip, selCategory===cat.id && { backgroundColor:cat.color, borderColor:cat.color }]}
            onPress={() => { setSelCategory(c=>c===cat.id?null:cat.id); handleSearch(cat.label+' jobs'); }}>
            <Ionicons name={cat.icon} size={16} color={selCategory===cat.id?'#FFF':cat.color} />
            <Text style={[s.catChipText, selCategory===cat.id && { color:'#FFF' }]}>{cat.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  // ─── Render: Trending searches ─────────────────────────────────────────────
  const renderTrending = () => !searched && (
    <View style={s.trendSection}>
      <Text style={s.catTitle}>🔥 Trending Searches</Text>
      <View style={s.trendGrid}>
        {TRENDING_SEARCHES.slice(0,6).map((t,i) => (
          <TouchableOpacity key={i} style={s.trendChip} onPress={() => { setQuery(t); handleSearch(t); }}>
            <Text style={s.trendText}>{t}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  // ─── Render: Suggested jobs (before search) ────────────────────────────────
  const renderSuggestedJobs = () => !searched && suggestedJobs.length > 0 && (
    <View style={s.suggestedSection}>
      <View style={s.sectionRow}>
        <Text style={s.catTitle}>Latest Engineering Jobs</Text>
        <TouchableOpacity onPress={() => handleSearch('engineering')}>
          <Text style={s.seeAll}>See All →</Text>
        </TouchableOpacity>
      </View>
      <FlatList
        data={suggestedJobs.slice(0,6)}
        keyExtractor={item => item.id}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal:16 }}
        renderItem={({ item }) => renderCompactJobCard(item)}
      />
    </View>
  );

  // ─── Render: Compact job card (horizontal) ─────────────────────────────────
  const renderCompactJobCard = (job) => {
    const saved    = savedIds.has(String(job.id));
    const applied  = appliedIds.has(String(job.id));
    const tc       = typeColor(job.type);
    return (
      <TouchableOpacity style={s.compactCard} onPress={() => openDetail(job)}>
        <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
          <View style={[s.compactType, { backgroundColor:tc.bg }]}>
            <Text style={[s.compactTypeText, { color:tc.text }]}>{job.type}</Text>
          </View>
          <TouchableOpacity onPress={()=>toggleSave(job)}>
            <Ionicons name={saved?'bookmark':'bookmark-outline'} size={16} color={saved?'#F59E0B':'#94a3b8'} />
          </TouchableOpacity>
        </View>
        <Text style={s.compactTitle} numberOfLines={2}>{job.title}</Text>
        <Text style={s.compactCompany} numberOfLines={1}>{job.company}</Text>
        <Text style={s.compactLoc} numberOfLines={1}>{job.location}</Text>
        <Text style={s.compactSalary}>{job.salary || 'Competitive'}</Text>
        {job.matchScore !== null && (
          <View style={[s.compactMatch, { backgroundColor:matchColor(job.matchScore)+'22' }]}>
            <Text style={[s.compactMatchText, { color:matchColor(job.matchScore) }]}>{job.matchScore}% match</Text>
          </View>
        )}
      </TouchableOpacity>
    );
  };

  // ─── Render: Full job card (list) ─────────────────────────────────────────
  const renderJobCard = ({ item: job }) => {
    const saved   = savedIds.has(String(job.id));
    const applied = appliedIds.has(String(job.id));
    const tc      = typeColor(job.type);
    const inComp  = compareJobs.some(j=>String(j.id)===String(job.id));

    return (
      <TouchableOpacity
        style={[s.jobCard, applied && s.jobCardApplied, inComp && s.jobCardCompare]}
        onPress={() => compareMode ? toggleCompare(job) : openDetail(job)}
        onLongPress={() => { setCompareMode(true); toggleCompare(job); }}
        activeOpacity={0.88}
      >
        {/* Top row: logo + title + save */}
        <View style={s.cardTop}>
          <View style={s.logoBox}>
            {job.companyLogo
              ? <Image source={{ uri: job.companyLogo.startsWith('http') ? job.companyLogo : undefined }} style={s.logoImg} onError={()=>{}} />
              : <MaterialCommunityIcons name="briefcase-outline" size={22} color="#0e4e96" />
            }
          </View>
          <View style={{ flex:1, marginLeft:12 }}>
            <Text style={s.cardTitle} numberOfLines={2}>{job.title}</Text>
            <Text style={s.cardCompany} numberOfLines={1}>{job.company}</Text>
          </View>
          <TouchableOpacity onPress={()=>toggleSave(job)} style={s.saveIconBtn}>
            <Ionicons name={saved?'bookmark':'bookmark-outline'} size={20} color={saved?'#F59E0B':'#94a3b8'} />
          </TouchableOpacity>
        </View>

        {/* Badges row */}
        <View style={s.badgesRow}>
          {job.isUrgent && <View style={s.urgentBadge}><Text style={s.urgentText}>🔥 URGENT</Text></View>}
          {applied      && <View style={s.appliedBadge}><Text style={s.appliedText}>✓ Applied</Text></View>}
          <View style={[s.typeBadge, { backgroundColor:tc.bg }]}>
            <Text style={[s.typeText, { color:tc.text }]}>{job.type}</Text>
          </View>
          {job.isRemote && <View style={s.remoteBadge}><Text style={s.remoteText}>Remote</Text></View>}
          {inComp && <View style={s.compareBadge}><Text style={s.compareText}>Compare</Text></View>}
        </View>

        {/* Meta info */}
        <View style={s.metaGrid}>
          <View style={s.metaItem}><Ionicons name="location-outline" size={13} color="#94a3b8" /><Text style={s.metaText} numberOfLines={1}>{job.location}</Text></View>
          <View style={s.metaItem}><MaterialCommunityIcons name="currency-gbp" size={13} color="#94a3b8" /><Text style={s.metaText}>{job.salary || 'Competitive'}</Text></View>
          <View style={s.metaItem}><Ionicons name="time-outline" size={13} color="#94a3b8" /><Text style={s.metaText}>{fmtAgo(job.posted)}</Text></View>
          {job.applicants && <View style={s.metaItem}><MaterialCommunityIcons name="account-multiple-outline" size={13} color="#94a3b8" /><Text style={s.metaText}>{job.applicants} applied</Text></View>}
        </View>

        {/* Skills */}
        {job.skills?.length > 0 && (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.skillsRow}>
            {job.skills.slice(0,5).map((sk,i) => {
              const isMatch = userSkills.some(us => sk.toLowerCase().includes(us.toLowerCase()));
              return (
                <View key={i} style={[s.skillChip, isMatch && s.skillChipMatch]}>
                  <Text style={[s.skillText, isMatch && s.skillTextMatch]}>{sk}</Text>
                </View>
              );
            })}
          </ScrollView>
        )}

        {/* Footer: match score + actions */}
        <View style={s.cardFooter}>
          {job.matchScore !== null && job.matchScore !== undefined
            ? <View style={[s.matchBadge, { backgroundColor:matchColor(job.matchScore)+'22', borderColor:matchColor(job.matchScore)+'55' }]}>
                <MaterialCommunityIcons name="brain" size={12} color={matchColor(job.matchScore)} />
                <Text style={[s.matchText, { color:matchColor(job.matchScore) }]}>{job.matchScore}% match</Text>
              </View>
            : <View />
          }
          <View style={s.cardActions}>
            <TouchableOpacity style={s.shareBtn} onPress={()=>handleShare(job)}>
              <Ionicons name="share-social-outline" size={16} color="#64748b" />
            </TouchableOpacity>
            <TouchableOpacity
              style={[s.applyBtn, applied && s.applyBtnApplied]}
              onPress={() => handleApply(job)}
              disabled={applied}
            >
              <Text style={s.applyBtnText}>{applied ? '✓ Applied' : 'Apply Now'}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  // ─── Render: Grid job card ─────────────────────────────────────────────────
  const renderGridCard = ({ item: job }) => {
    const saved   = savedIds.has(String(job.id));
    const applied = appliedIds.has(String(job.id));
    const tc      = typeColor(job.type);
    return (
      <TouchableOpacity style={s.gridCard} onPress={() => openDetail(job)}>
        <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:6 }}>
          <View style={[s.typeBadge, { backgroundColor:tc.bg }]}>
            <Text style={[s.typeText, { color:tc.text }]}>{job.type}</Text>
          </View>
          <TouchableOpacity onPress={()=>toggleSave(job)}>
            <Ionicons name={saved?'bookmark':'bookmark-outline'} size={16} color={saved?'#F59E0B':'#94a3b8'} />
          </TouchableOpacity>
        </View>
        <Text style={s.gridTitle} numberOfLines={2}>{job.title}</Text>
        <Text style={s.gridCompany} numberOfLines={1}>{job.company}</Text>
        <View style={s.metaItem}><Ionicons name="location-outline" size={12} color="#94a3b8" /><Text style={[s.metaText,{fontSize:11}]} numberOfLines={1}>{job.location}</Text></View>
        <Text style={s.gridSalary}>{job.salary||'Competitive'}</Text>
        {job.matchScore !== null && (
          <View style={[s.gridMatch, { backgroundColor:matchColor(job.matchScore)+'22' }]}>
            <Text style={[s.gridMatchText,{color:matchColor(job.matchScore)}]}>{job.matchScore}%</Text>
          </View>
        )}
        {applied && <View style={s.gridApplied}><Text style={s.gridAppliedText}>✓</Text></View>}
      </TouchableOpacity>
    );
  };

  // ─── Render: Job detail modal ──────────────────────────────────────────────
  const renderDetailModal = () => {
    const job = selectedJob;
    if (!job) return null;
    const saved   = savedIds.has(String(job.id));
    const applied = appliedIds.has(String(job.id));
    const tc      = typeColor(job.type);

    return (
      <Modal visible={detailModal} animationType="slide" transparent onRequestClose={()=>setDetailModal(false)}>
        <View style={s.modalOverlay}>
          <View style={[s.modalSheet, { maxHeight:'92%' }]}>
            {/* Header */}
            <View style={s.modalHeader}>
              <TouchableOpacity onPress={()=>setDetailModal(false)} style={s.modalBack}>
                <Ionicons name="close" size={22} color="#64748b" />
              </TouchableOpacity>
              <Text style={s.modalHeaderTitle} numberOfLines={1}>{job.title}</Text>
              <View style={{ flexDirection:'row', gap:10 }}>
                <TouchableOpacity onPress={()=>handleShare(job)}><Ionicons name="share-social-outline" size={20} color="#64748b" /></TouchableOpacity>
                <TouchableOpacity onPress={()=>toggleSave(job)}><Ionicons name={saved?'bookmark':'bookmark-outline'} size={20} color={saved?'#F59E0B':'#64748b'} /></TouchableOpacity>
              </View>
            </View>

            <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom:120 }}>
              {/* Company hero */}
              <View style={s.detailHero}>
                <View style={s.detailLogoBox}>
                  {job.companyLogo
                    ? <Image source={{ uri: job.companyLogo.startsWith('http')?job.companyLogo:undefined }} style={{ width:60,height:60,borderRadius:12 }} onError={()=>{}} />
                    : <MaterialCommunityIcons name="briefcase" size={30} color="#0e4e96" />
                  }
                </View>
                <Text style={s.detailTitle}>{job.title}</Text>
                <Text style={s.detailCompany}>{job.company}</Text>
                <View style={{ flexDirection:'row', alignItems:'center', gap:6, marginTop:4 }}>
                  <Ionicons name="location-outline" size={14} color="#64748b" />
                  <Text style={s.detailMeta}>{job.location}</Text>
                </View>
                {job.matchScore !== null && (
                  <View style={[s.detailMatchBox, { backgroundColor:matchColor(job.matchScore)+'22' }]}>
                    <MaterialCommunityIcons name="brain" size={14} color={matchColor(job.matchScore)} />
                    <Text style={[s.detailMatchText,{color:matchColor(job.matchScore)}]}>{job.matchScore}% Profile Match</Text>
                    <View style={[s.detailMatchBar,{flex:1}]}>
                      <View style={[s.detailMatchFill,{width:`${job.matchScore}%`,backgroundColor:matchColor(job.matchScore)}]} />
                    </View>
                  </View>
                )}
              </View>

              {/* Key stats */}
              <View style={s.keyStats}>
                {[
                  { icon:'currency-gbp',   label:'Salary',     val: job.salary||'Competitive' },
                  { icon:'briefcase-outline',label:'Type',     val: job.type||'Full-time'     },
                  { icon:'school-outline', label:'Level',      val: job.experience||'Any'     },
                  { icon:'clock-outline',  label:'Posted',     val: fmtAgo(job.posted)        },
                ].map((k,i) => (
                  <View key={i} style={s.keyStat}>
                    <MaterialCommunityIcons name={k.icon} size={18} color="#0e4e96" />
                    <Text style={s.keyStatLabel}>{k.label}</Text>
                    <Text style={s.keyStatVal} numberOfLines={1}>{k.val}</Text>
                  </View>
                ))}
              </View>

              {/* Badges */}
              <View style={[s.badgesRow,{paddingHorizontal:16}]}>
                {job.isUrgent && <View style={s.urgentBadge}><Text style={s.urgentText}>🔥 Urgent</Text></View>}
                {job.isRemote && <View style={s.remoteBadge}><Text style={s.remoteText}>Remote</Text></View>}
                {job.deadline && <View style={s.deadlineBadge}><Ionicons name="calendar-outline" size={12} color="#EF4444" /><Text style={s.deadlineText}>Closes {fmtAgo(job.deadline)}</Text></View>}
                {job.applicants && <View style={s.applCountBadge}><Text style={s.applCountText}>{job.applicants} applicants</Text></View>}
              </View>

              {/* Description */}
              {job.description ? (
                <View style={s.detailSection}>
                  <Text style={s.detailSectionTitle}>Job Description</Text>
                  <Text style={s.detailBody}>{job.description}</Text>
                </View>
              ) : null}

              {/* Skills */}
              {job.skills?.length > 0 && (
                <View style={s.detailSection}>
                  <Text style={s.detailSectionTitle}>Required Skills</Text>
                  <View style={s.skillsWrap}>
                    {job.skills.map((sk,i) => {
                      const isMatch = userSkills.some(us=>sk.toLowerCase().includes(us.toLowerCase()));
                      return (
                        <View key={i} style={[s.detailSkill, isMatch && s.detailSkillMatch]}>
                          {isMatch && <Ionicons name="checkmark-circle" size={12} color="#10B981" style={{marginRight:4}} />}
                          <Text style={[s.detailSkillText, isMatch && {color:'#10B981'}]}>{sk}</Text>
                        </View>
                      );
                    })}
                  </View>
                  {userSkills.length > 0 && (
                    <Text style={s.skillGapHint}>
                      {job.skills.filter(sk=>!userSkills.some(us=>sk.toLowerCase().includes(us.toLowerCase()))).length > 0
                        ? `⚠️ Missing: ${job.skills.filter(sk=>!userSkills.some(us=>sk.toLowerCase().includes(us.toLowerCase()))).slice(0,3).join(', ')}`
                        : '✅ You have all required skills!'
                      }
                    </Text>
                  )}
                </View>
              )}

              {/* Requirements */}
              {job.requirements?.length > 0 && (
                <View style={s.detailSection}>
                  <Text style={s.detailSectionTitle}>Requirements</Text>
                  {job.requirements.map((r,i) => (
                    <View key={i} style={s.bulletRow}>
                      <View style={s.bullet} />
                      <Text style={s.bulletText}>{r}</Text>
                    </View>
                  ))}
                </View>
              )}

              {/* Benefits */}
              {job.benefits?.length > 0 && (
                <View style={s.detailSection}>
                  <Text style={s.detailSectionTitle}>Benefits</Text>
                  <View style={s.benefitsGrid}>
                    {job.benefits.map((b,i) => (
                      <View key={i} style={s.benefitItem}>
                        <MaterialCommunityIcons name="check-circle" size={14} color="#10B981" />
                        <Text style={s.benefitText}>{b}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}
            </ScrollView>

            {/* Sticky CTA */}
            <View style={s.detailCTA}>
              <TouchableOpacity style={s.detailSaveBtn} onPress={()=>toggleSave(job)}>
                <Ionicons name={saved?'bookmark':'bookmark-outline'} size={20} color="#0e4e96" />
              </TouchableOpacity>
              <TouchableOpacity
                style={[s.detailApplyBtn, applied && s.detailApplyBtnApplied]}
                onPress={() => handleApply(job)}
                disabled={applied}
              >
                <Text style={s.detailApplyText}>{applied ? '✓ Already Applied' : 'Apply for this Role'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  // ─── Render: Filter modal ──────────────────────────────────────────────────
  const renderFilterModal = () => (
    <Modal visible={filterModal} animationType="slide" transparent onRequestClose={()=>setFilterModal(false)}>
      <View style={s.modalOverlay}>
        <View style={[s.modalSheet, { maxHeight:'88%' }]}>
          <View style={s.modalHeader}>
            <Text style={s.modalHeaderTitle}>Filter & Sort</Text>
            <TouchableOpacity onPress={()=>setFilterModal(false)}><Ionicons name="close" size={24} color="#64748b" /></TouchableOpacity>
          </View>
          <ScrollView contentContainerStyle={{ padding:16, paddingBottom:20 }}>
            {[
              { title:'Sort By',           opts:SORT_OPTIONS,   state:selSort,   set:setSelSort   },
              { title:'Job Type',           opts:JOB_TYPES,      state:selType,   set:setSelType   },
              { title:'Experience Level',   opts:EXP_LEVELS,     state:selExp,    set:setSelExp    },
              { title:'Date Posted',        opts:DATE_POSTED,    state:selDate,   set:setSelDate   },
            ].map(section => (
              <View key={section.title} style={{ marginBottom:16 }}>
                <Text style={s.filterSection}>{section.title}</Text>
                <View style={s.filterGrid}>
                  {section.opts.map(opt => (
                    <TouchableOpacity key={opt} style={[s.optChip, section.state===opt && s.optChipActive]} onPress={()=>section.set(opt)}>
                      <Text style={[s.optChipText, section.state===opt && s.optChipTextActive]}>{opt}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            ))}

            {/* Salary */}
            <Text style={s.filterSection}>Salary Range</Text>
            <View style={s.filterGrid}>
              {SALARY_RANGES.map((r,i) => (
                <TouchableOpacity key={i} style={[s.optChip, selSalary===i && s.optChipActive]} onPress={()=>setSelSalary(i)}>
                  <Text style={[s.optChipText, selSalary===i && s.optChipTextActive]}>{r.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Toggles */}
            {[
              { label:'Remote Only', sub:'Show only remote positions', state:remoteOnly, set:setRemoteOnly },
              { label:'Urgent Only', sub:'Roles closing within 7 days', state:urgentOnly, set:setUrgentOnly },
            ].map(t => (
              <View key={t.label} style={s.toggleRow}>
                <View style={{ flex:1 }}>
                  <Text style={s.filterSection}>{t.label}</Text>
                  <Text style={{ fontSize:12, color:'#94a3b8' }}>{t.sub}</Text>
                </View>
                <Switch value={t.state} onValueChange={t.set} trackColor={{false:'#E5E7EB',true:'#BFDBFE'}} thumbColor={t.state?'#0e4e96':'#D1D5DB'} />
              </View>
            ))}
          </ScrollView>

          <View style={s.modalFooter}>
            <TouchableOpacity style={s.footerResetBtn} onPress={resetFilters}>
              <Text style={s.footerResetText}>Reset All</Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.footerApplyBtn} onPress={()=>{ setFilterModal(false); handleSearch(); }}>
              <Text style={s.footerApplyText}>Apply Filters</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // ─── Render: Alert modal ───────────────────────────────────────────────────
  const renderAlertModal = () => (
    <Modal visible={alertModal} animationType="slide" transparent onRequestClose={()=>setAlertModal(false)}>
      <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'} style={s.modalOverlay}>
        <View style={[s.modalSheet, { maxHeight:420 }]}>
          <View style={s.modalHeader}>
            <Text style={s.modalHeaderTitle}>🔔 Create Job Alert</Text>
            <TouchableOpacity onPress={()=>setAlertModal(false)}><Ionicons name="close" size={24} color="#64748b" /></TouchableOpacity>
          </View>
          <View style={{ padding:16 }}>
            <Text style={s.filterSection}>Alert Keywords</Text>
            <View style={[s.searchBox,{marginBottom:14}]}>
              <Ionicons name="search-outline" size={16} color="#94a3b8" style={{marginLeft:10}} />
              <TextInput style={s.searchInput} placeholder={query||"e.g. Python developer"} placeholderTextColor="#CBD5E1"
                value={alertForm.title} onChangeText={v=>setAlertForm(f=>({...f,title:v}))} />
            </View>
            <Text style={s.filterSection}>Notification Frequency</Text>
            <View style={s.filterGrid}>
              {ALERT_FREQS.map(f => (
                <TouchableOpacity key={f} style={[s.optChip, alertForm.frequency===f && s.optChipActive]} onPress={()=>setAlertForm(af=>({...af,frequency:f}))}>
                  <Text style={[s.optChipText, alertForm.frequency===f && s.optChipTextActive]}>{f}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <Text style={{ fontSize:12, color:'#94a3b8', marginTop:10 }}>
              Based on: {[query,locationQuery,selType!=='All'&&selType].filter(Boolean).join(' · ')||'No active filters'}
            </Text>
          </View>
          <View style={s.modalFooter}>
            <TouchableOpacity style={s.footerResetBtn} onPress={()=>setAlertModal(false)}>
              <Text style={s.footerResetText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.footerApplyBtn} onPress={createAlert} disabled={savingAlert}>
              {savingAlert ? <ActivityIndicator size={16} color="#FFF" /> : <Text style={s.footerApplyText}>Create Alert</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );

  // ─── Render: Compare modal ─────────────────────────────────────────────────
  const renderCompareModal = () => {
    if (compareJobs.length < 2) return null;
    const [a, b] = compareJobs;
    return (
      <Modal visible={compareModal} animationType="slide" transparent onRequestClose={()=>setCompareModal(false)}>
        <View style={s.modalOverlay}>
          <View style={[s.modalSheet,{maxHeight:'85%'}]}>
            <View style={s.modalHeader}>
              <Text style={s.modalHeaderTitle}>Compare Jobs</Text>
              <TouchableOpacity onPress={()=>setCompareModal(false)}><Ionicons name="close" size={24} color="#64748b" /></TouchableOpacity>
            </View>
            {/* Header */}
            <View style={s.compareHeader}>
              {[a,b].map((j,i) => (
                <View key={i} style={s.compareJobHeader}>
                  <MaterialCommunityIcons name="briefcase" size={24} color="#0e4e96" />
                  <Text style={s.compareJobTitle} numberOfLines={2}>{j.title}</Text>
                  <Text style={s.compareJobCo}>{j.company}</Text>
                </View>
              ))}
            </View>
            <ScrollView>
              {[
                { label:'Salary',    va:a.salary||'—',        vb:b.salary||'—'         },
                { label:'Location',  va:a.location,            vb:b.location            },
                { label:'Type',      va:a.type,                vb:b.type                },
                { label:'Level',     va:a.experience||'Any',  vb:b.experience||'Any'   },
                { label:'Posted',    va:fmtAgo(a.posted),      vb:fmtAgo(b.posted)      },
                { label:'Applicants',va:String(a.applicants||'—'), vb:String(b.applicants||'—') },
                { label:'Match',     va:a.matchScore!=null?`${a.matchScore}%`:'—', vb:b.matchScore!=null?`${b.matchScore}%`:'—', highlight:true },
              ].map((row,i) => (
                <View key={i} style={[s.compareRow, i%2===0 && { backgroundColor:'#F9FAFB' }]}>
                  <Text style={[s.compareCell,{color:row.highlight?matchColor(parseInt(row.va)||0):'#1e293b'}]}>{row.va}</Text>
                  <Text style={s.compareLabel}>{row.label}</Text>
                  <Text style={[s.compareCell,{color:row.highlight?matchColor(parseInt(row.vb)||0):'#1e293b'}]}>{row.vb}</Text>
                </View>
              ))}
            </ScrollView>
            <View style={s.modalFooter}>
              <TouchableOpacity style={s.footerResetBtn} onPress={()=>{setCompareJobs([]);setCompareMode(false);setCompareModal(false);}}>
                <Text style={s.footerResetText}>Clear</Text>
              </TouchableOpacity>
              <TouchableOpacity style={s.footerApplyBtn} onPress={()=>{ setCompareModal(false); openDetail(a.matchScore>=(b.matchScore||0)?a:b); }}>
                <Text style={s.footerApplyText}>View Best Match</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  // ─── Main render ───────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={s.root}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {renderHeader()}

      <FlatList
        data={searched ? results : []}
        keyExtractor={item => item.id}
        numColumns={viewMode === 'grid' ? 2 : 1}
        key={viewMode} // Force re-render when switching modes
        columnWrapperStyle={viewMode==='grid' ? { gap:10, paddingHorizontal:12, marginBottom:10 } : undefined}
        contentContainerStyle={{ paddingBottom:100 }}
        ListHeaderComponent={() => (
          <Animated.View style={{ opacity: fadeAnim }}>
            {renderSearchBar()}
            {renderSuggestions()}
            {renderCategories()}
            {renderTrending()}
            {renderSuggestedJobs()}

            {/* Compare FAB */}
            {compareMode && compareJobs.length > 0 && (
              <TouchableOpacity style={s.compareFAB} onPress={()=>compareJobs.length===2?setCompareModal(true):Alert.alert('Select 2','Please select 2 jobs to compare.')}>
                <MaterialCommunityIcons name="compare" size={18} color="#FFF" />
                <Text style={s.compareFABText}>Compare ({compareJobs.length}/2)</Text>
                <TouchableOpacity onPress={()=>{setCompareJobs([]);setCompareMode(false);}}>
                  <Ionicons name="close-circle" size={18} color="rgba(255,255,255,0.7)" />
                </TouchableOpacity>
              </TouchableOpacity>
            )}

            {/* Results header */}
            {searched && !loading && (
              <View style={s.resultsHeader}>
                <Text style={s.resultsCount}>{results.length}{totalCount>results.length?` of ${totalCount}`:''} {results.length === 1 ? 'job' : 'jobs'} found</Text>
                <TouchableOpacity style={s.sortBtn} onPress={()=>setFilterModal(true)}>
                  <MaterialCommunityIcons name="sort" size={16} color="#0e4e96" />
                  <Text style={s.sortBtnText}>{selSort}</Text>
                </TouchableOpacity>
              </View>
            )}

            {loading && <ActivityIndicator size={36} color="#0e4e96" style={{ marginVertical:30 }} />}

            {error && !loading && (
              <View style={s.errorBox}>
                <MaterialCommunityIcons name="alert-circle-outline" size={44} color="#EF4444" />
                <Text style={s.errorText}>{error}</Text>
                <TouchableOpacity style={s.retryBtn} onPress={()=>handleSearch()}>
                  <Text style={s.retryText}>Try Again</Text>
                </TouchableOpacity>
              </View>
            )}

            {searched && !loading && !error && results.length === 0 && (
              <View style={s.emptyBox}>
                <MaterialCommunityIcons name="file-search-outline" size={60} color="#CBD5E1" />
                <Text style={s.emptyTitle}>No jobs found</Text>
                <Text style={s.emptySubtitle}>Try different keywords or adjust your filters</Text>
                <TouchableOpacity style={s.emptyResetBtn} onPress={resetFilters}>
                  <Text style={s.emptyResetText}>Reset Filters</Text>
                </TouchableOpacity>
              </View>
            )}
          </Animated.View>
        )}
        renderItem={viewMode === 'grid' ? renderGridCard : renderJobCard}
        ListFooterComponent={() => (
          <>
            {loadingMore && <ActivityIndicator size={28} color="#0e4e96" style={{ marginVertical:16 }} />}
            {hasMore && !loadingMore && results.length > 0 && (
              <TouchableOpacity style={s.loadMoreBtn} onPress={loadMore}>
                <Text style={s.loadMoreText}>Load More Jobs</Text>
              </TouchableOpacity>
            )}
          </>
        )}
        refreshControl={<RefreshControl refreshing={false} onRefresh={()=>searched&&handleSearch()} colors={['#0e4e96']} />}
      />

      {renderDetailModal()}
      {renderFilterModal()}
      {renderAlertModal()}
      {renderCompareModal()}
    </SafeAreaView>
  );
}

// ─── Small components ─────────────────────────────────────────────────────────
const FilterChip = ({ label, onRemove }) => (
  <TouchableOpacity style={fc.chip} onPress={onRemove}>
    <Text style={fc.text}>{label}</Text>
    <Ionicons name="close" size={11} color="#0e4e96" />
  </TouchableOpacity>
);
const fc = StyleSheet.create({
  chip: { flexDirection:'row', alignItems:'center', gap:4, backgroundColor:'#EFF6FF', borderWidth:1, borderColor:'#BFDBFE', paddingHorizontal:10, paddingVertical:5, borderRadius:16, marginRight:8 },
  text: { fontSize:12, color:'#0e4e96', fontWeight:'500' },
});

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  root:         { flex:1, backgroundColor:'#F8FAFC' },
  // Header
  header:       { flexDirection:'row', alignItems:'center', backgroundColor:'#0e4e96', paddingVertical:12, paddingHorizontal:14 },
  headerBack:   { width:38, height:38, borderRadius:19, backgroundColor:'rgba(255,255,255,0.15)', justifyContent:'center', alignItems:'center' },
  headerCenter: { flex:1, flexDirection:'row', alignItems:'center', justifyContent:'center', gap:8 },
  headerTitle:  { fontSize:18, fontWeight:'700', color:'#FFF' },
  headerRight:  { flexDirection:'row', gap:8 },
  headerIcon:   { width:38, height:38, borderRadius:19, backgroundColor:'rgba(255,255,255,0.15)', justifyContent:'center', alignItems:'center' },
  // Search section
  searchSection:{ backgroundColor:'#FFF', paddingHorizontal:14, paddingVertical:14, borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  searchRow:    { flexDirection:'row', gap:10, marginBottom:10 },
  locationRow:  { flexDirection:'row', gap:10, alignItems:'center' },
  searchBox:    { flex:1, flexDirection:'row', alignItems:'center', backgroundColor:'#F8FAFC', borderRadius:10, borderWidth:1, borderColor:'#E2E8F0', height:44 },
  searchInput:  { flex:1, fontSize:14, color:'#1e293b', paddingHorizontal:8 },
  filterBtn:    { width:44, height:44, borderRadius:10, borderWidth:1.5, borderColor:'#0e4e96', justifyContent:'center', alignItems:'center', position:'relative' },
  filterBtnActive:{ backgroundColor:'#0e4e96' },
  filterBadge:  { position:'absolute', top:-6, right:-6, backgroundColor:'#EF4444', borderRadius:8, width:18, height:18, justifyContent:'center', alignItems:'center' },
  filterBadgeText:{ fontSize:9, color:'#FFF', fontWeight:'bold' },
  nearbyToggle: { flexDirection:'row', alignItems:'center', gap:4 },
  // GPS status
  gpsStatus:    { backgroundColor:'#F0FDF4', borderRadius:8, padding:10, marginTop:8, gap:6 },
  gpsText:      { fontSize:12, color:'#374151', flex:1 },
  radiusChip:   { borderRadius:14, borderWidth:1, borderColor:'#D1D5DB', paddingHorizontal:10, paddingVertical:4, marginRight:6, backgroundColor:'#FFF' },
  radiusChipActive:{ backgroundColor:'#0e4e96', borderColor:'#0e4e96' },
  radiusText:   { fontSize:11, color:'#374151' },
  radiusTextActive:{ color:'#FFF' },
  // Active filter chips
  activeFilters:{ marginTop:8, flexGrow:0 },
  clearAll:     { paddingHorizontal:8, paddingVertical:4 },
  clearAllText: { fontSize:12, color:'#EF4444', fontWeight:'600' },
  // Search button
  searchBtn:    { flexDirection:'row', alignItems:'center', justifyContent:'center', backgroundColor:'#0e4e96', borderRadius:10, paddingVertical:12, marginTop:12, gap:8 },
  searchBtnText:{ fontSize:15, fontWeight:'700', color:'#FFF' },
  // Suggestions
  suggestionsBox:{ backgroundColor:'#FFF', marginHorizontal:14, borderRadius:10, borderWidth:1, borderColor:'#E2E8F0', zIndex:100, position:'absolute', top:200, left:0, right:0, shadowColor:'#000', shadowOffset:{width:0,height:4}, shadowOpacity:0.12, shadowRadius:8, elevation:8 },
  suggRow:      { flexDirection:'row', alignItems:'center', gap:10, paddingHorizontal:14, paddingVertical:10, borderBottomWidth:1, borderBottomColor:'#F1F5F9' },
  suggText:     { flex:1, fontSize:13, color:'#374151' },
  suggSection:  { fontSize:11, fontWeight:'700', color:'#94a3b8', paddingHorizontal:14, paddingTop:8, paddingBottom:4, textTransform:'uppercase' },
  // Categories
  catSection:   { paddingVertical:14 },
  catTitle:     { fontSize:16, fontWeight:'700', color:'#1e293b', paddingHorizontal:16, marginBottom:10 },
  catChip:      { flexDirection:'row', alignItems:'center', gap:6, paddingHorizontal:14, paddingVertical:8, borderRadius:20, backgroundColor:'#F1F5F9', borderWidth:1, borderColor:'#E2E8F0' },
  catChipText:  { fontSize:13, color:'#374151', fontWeight:'500' },
  // Trending
  trendSection: { paddingHorizontal:16, paddingBottom:14 },
  trendGrid:    { flexDirection:'row', flexWrap:'wrap', gap:8 },
  trendChip:    { backgroundColor:'#EFF6FF', borderRadius:20, paddingHorizontal:14, paddingVertical:6, borderWidth:1, borderColor:'#BFDBFE' },
  trendText:    { fontSize:12, color:'#1d4ed8', fontWeight:'500' },
  // Suggested jobs
  suggestedSection:{ paddingBottom:16 },
  sectionRow:   { flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingHorizontal:16, marginBottom:10 },
  seeAll:       { fontSize:13, color:'#0e4e96', fontWeight:'600' },
  // Compact card (horizontal)
  compactCard:  { width:160, backgroundColor:'#FFF', borderRadius:12, padding:12, marginRight:10, shadowColor:'#000', shadowOffset:{width:0,height:2}, shadowOpacity:0.07, shadowRadius:4, elevation:2 },
  compactType:  { borderRadius:10, paddingHorizontal:8, paddingVertical:3 },
  compactTypeText:{ fontSize:10, fontWeight:'600' },
  compactTitle: { fontSize:13, fontWeight:'700', color:'#1e293b', marginBottom:3 },
  compactCompany:{ fontSize:11, color:'#0e4e96', fontWeight:'600', marginBottom:2 },
  compactLoc:   { fontSize:11, color:'#64748b', marginBottom:4 },
  compactSalary:{ fontSize:12, fontWeight:'700', color:'#374151', marginBottom:6 },
  compactMatch: { borderRadius:10, paddingHorizontal:8, paddingVertical:3 },
  compactMatchText:{ fontSize:10, fontWeight:'700' },
  // Results header
  resultsHeader:{ flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingHorizontal:16, paddingVertical:10 },
  resultsCount: { fontSize:15, fontWeight:'700', color:'#374151' },
  sortBtn:      { flexDirection:'row', alignItems:'center', gap:4 },
  sortBtnText:  { fontSize:12, color:'#0e4e96', fontWeight:'600' },
  // Full job card
  jobCard:      { backgroundColor:'#FFF', borderRadius:14, padding:14, marginHorizontal:12, marginBottom:10, shadowColor:'#000', shadowOffset:{width:0,height:2}, shadowOpacity:0.06, shadowRadius:6, elevation:2 },
  jobCardApplied:{ borderLeftWidth:3, borderLeftColor:'#10B981' },
  jobCardCompare:{ borderWidth:2, borderColor:'#0e4e96' },
  cardTop:      { flexDirection:'row', alignItems:'flex-start', marginBottom:8 },
  logoBox:      { width:44, height:44, borderRadius:10, backgroundColor:'#EFF6FF', justifyContent:'center', alignItems:'center' },
  logoImg:      { width:44, height:44, borderRadius:10 },
  cardTitle:    { fontSize:15, fontWeight:'700', color:'#1e293b', marginBottom:2 },
  cardCompany:  { fontSize:13, color:'#0e4e96', fontWeight:'600' },
  saveIconBtn:  { padding:4 },
  badgesRow:    { flexDirection:'row', flexWrap:'wrap', gap:6, marginBottom:8 },
  urgentBadge:  { flexDirection:'row', alignItems:'center', backgroundColor:'#FEF2F2', borderRadius:10, paddingHorizontal:8, paddingVertical:3 },
  urgentText:   { fontSize:10, fontWeight:'700', color:'#DC2626' },
  appliedBadge: { backgroundColor:'#F0FDF4', borderRadius:10, paddingHorizontal:8, paddingVertical:3, borderWidth:1, borderColor:'#86EFAC' },
  appliedText:  { fontSize:10, fontWeight:'600', color:'#16A34A' },
  typeBadge:    { borderRadius:10, paddingHorizontal:8, paddingVertical:3 },
  typeText:     { fontSize:10, fontWeight:'600' },
  remoteBadge:  { backgroundColor:'#EFF6FF', borderRadius:10, paddingHorizontal:8, paddingVertical:3 },
  remoteText:   { fontSize:10, fontWeight:'600', color:'#2563EB' },
  compareBadge: { backgroundColor:'#EEF2FF', borderRadius:10, paddingHorizontal:8, paddingVertical:3, borderWidth:1, borderColor:'#C7D2FE' },
  compareText:  { fontSize:10, fontWeight:'600', color:'#0e4e96' },
  deadlineBadge:{ flexDirection:'row', alignItems:'center', gap:3, backgroundColor:'#FEF2F2', borderRadius:10, paddingHorizontal:8, paddingVertical:3 },
  deadlineText: { fontSize:10, color:'#EF4444', fontWeight:'600' },
  applCountBadge:{ backgroundColor:'#F8FAFC', borderRadius:10, paddingHorizontal:8, paddingVertical:3 },
  applCountText:{ fontSize:10, color:'#64748b' },
  metaGrid:     { flexWrap:'wrap', flexDirection:'row', gap:8, marginBottom:8 },
  metaItem:     { flexDirection:'row', alignItems:'center', gap:4 },
  metaText:     { fontSize:12, color:'#64748b', maxWidth:120 },
  skillsRow:    { marginBottom:10 },
  skillChip:    { backgroundColor:'#F1F5F9', borderRadius:8, paddingHorizontal:9, paddingVertical:3, marginRight:6 },
  skillChipMatch:{ backgroundColor:'#F0FDF4', borderWidth:1, borderColor:'#86EFAC' },
  skillText:    { fontSize:11, color:'#475569' },
  skillTextMatch:{ color:'#10B981', fontWeight:'600' },
  cardFooter:   { flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingTop:8, borderTopWidth:1, borderTopColor:'#F1F5F9' },
  matchBadge:   { flexDirection:'row', alignItems:'center', gap:4, borderRadius:10, paddingHorizontal:8, paddingVertical:4, borderWidth:1 },
  matchText:    { fontSize:11, fontWeight:'700' },
  cardActions:  { flexDirection:'row', alignItems:'center', gap:8 },
  shareBtn:     { width:34, height:34, borderRadius:8, borderWidth:1, borderColor:'#E2E8F0', justifyContent:'center', alignItems:'center' },
  applyBtn:     { backgroundColor:'#0e4e96', borderRadius:10, paddingHorizontal:16, paddingVertical:8 },
  applyBtnApplied:{ backgroundColor:'#10B981' },
  applyBtnText: { color:'#FFF', fontSize:13, fontWeight:'700' },
  // Grid card
  gridCard:     { flex:1, backgroundColor:'#FFF', borderRadius:12, padding:12, shadowColor:'#000', shadowOffset:{width:0,height:2}, shadowOpacity:0.06, shadowRadius:4, elevation:2, position:'relative' },
  gridTitle:    { fontSize:13, fontWeight:'700', color:'#1e293b', marginTop:4, marginBottom:2 },
  gridCompany:  { fontSize:11, color:'#0e4e96', fontWeight:'600', marginBottom:4 },
  gridSalary:   { fontSize:12, fontWeight:'700', color:'#374151', marginTop:4 },
  gridMatch:    { marginTop:6, borderRadius:10, paddingHorizontal:8, paddingVertical:3, alignSelf:'flex-start' },
  gridMatchText:{ fontSize:10, fontWeight:'700' },
  gridApplied:  { position:'absolute', top:8, right:8, backgroundColor:'#10B981', borderRadius:10, width:20, height:20, justifyContent:'center', alignItems:'center' },
  gridAppliedText:{ fontSize:10, fontWeight:'bold', color:'#FFF' },
  // Compare FAB
  compareFAB:   { flexDirection:'row', alignItems:'center', backgroundColor:'#0e4e96', borderRadius:24, marginHorizontal:16, marginVertical:8, paddingHorizontal:16, paddingVertical:10, gap:8 },
  compareFABText:{ flex:1, color:'#FFF', fontWeight:'700', fontSize:13 },
  // Error / empty
  errorBox:     { alignItems:'center', paddingVertical:40, paddingHorizontal:32 },
  errorText:    { fontSize:15, color:'#EF4444', textAlign:'center', marginTop:10 },
  retryBtn:     { marginTop:14, backgroundColor:'#EF4444', borderRadius:10, paddingHorizontal:20, paddingVertical:10 },
  retryText:    { color:'#FFF', fontWeight:'600' },
  emptyBox:     { alignItems:'center', paddingVertical:50, paddingHorizontal:32 },
  emptyTitle:   { fontSize:18, fontWeight:'700', color:'#374151', marginTop:14 },
  emptySubtitle:{ fontSize:13, color:'#94a3b8', textAlign:'center', marginTop:6, marginBottom:16 },
  emptyResetBtn:{ backgroundColor:'#0e4e96', borderRadius:10, paddingHorizontal:24, paddingVertical:10 },
  emptyResetText:{ color:'#FFF', fontWeight:'600' },
  loadMoreBtn:  { backgroundColor:'#F8FAFC', borderWidth:1, borderColor:'#E2E8F0', borderRadius:10, marginHorizontal:16, marginVertical:12, paddingVertical:12, alignItems:'center' },
  loadMoreText: { fontSize:14, color:'#0e4e96', fontWeight:'600' },
  // Job detail modal
  modalOverlay: { flex:1, backgroundColor:'rgba(0,0,0,0.5)', justifyContent:'flex-end' },
  modalSheet:   { backgroundColor:'#FFF', borderTopLeftRadius:24, borderTopRightRadius:24, overflow:'hidden' },
  modalHeader:  { flexDirection:'row', alignItems:'center', padding:18, borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  modalBack:    { padding:4 },
  modalHeaderTitle:{ flex:1, fontSize:16, fontWeight:'700', color:'#1e293b', marginHorizontal:10 },
  detailHero:   { alignItems:'center', paddingVertical:24, paddingHorizontal:20, borderBottomWidth:1, borderBottomColor:'#F1F5F9' },
  detailLogoBox:{ width:70, height:70, borderRadius:14, backgroundColor:'#EFF6FF', justifyContent:'center', alignItems:'center', marginBottom:12 },
  detailTitle:  { fontSize:20, fontWeight:'800', color:'#1e293b', textAlign:'center', marginBottom:4 },
  detailCompany:{ fontSize:15, color:'#0e4e96', fontWeight:'600', marginBottom:4 },
  detailMeta:   { fontSize:13, color:'#64748b' },
  detailMatchBox:{ flexDirection:'row', alignItems:'center', gap:6, borderRadius:10, paddingHorizontal:12, paddingVertical:8, marginTop:12, width:'100%' },
  detailMatchText:{ fontSize:12, fontWeight:'700' },
  detailMatchBar:{ height:6, backgroundColor:'#E2E8F0', borderRadius:3, overflow:'hidden' },
  detailMatchFill:{ height:6, borderRadius:3 },
  keyStats:     { flexDirection:'row', borderTopWidth:1, borderBottomWidth:1, borderColor:'#F1F5F9' },
  keyStat:      { flex:1, alignItems:'center', paddingVertical:14, gap:3, borderRightWidth:1, borderRightColor:'#F1F5F9' },
  keyStatLabel: { fontSize:10, color:'#94a3b8', fontWeight:'500' },
  keyStatVal:   { fontSize:12, color:'#1e293b', fontWeight:'700', textAlign:'center' },
  detailSection:{ paddingHorizontal:16, paddingTop:18 },
  detailSectionTitle:{ fontSize:15, fontWeight:'700', color:'#1e293b', marginBottom:10, borderBottomWidth:2, borderBottomColor:'#0e4e96', paddingBottom:4 },
  detailBody:   { fontSize:14, color:'#475569', lineHeight:22 },
  skillsWrap:   { flexDirection:'row', flexWrap:'wrap', gap:8 },
  detailSkill:  { flexDirection:'row', alignItems:'center', backgroundColor:'#F1F5F9', borderRadius:8, paddingHorizontal:10, paddingVertical:6 },
  detailSkillMatch:{ backgroundColor:'#F0FDF4', borderWidth:1, borderColor:'#86EFAC' },
  detailSkillText:{ fontSize:12, color:'#475569', fontWeight:'500' },
  skillGapHint: { fontSize:12, color:'#64748b', marginTop:10, fontStyle:'italic' },
  bulletRow:    { flexDirection:'row', alignItems:'flex-start', gap:8, marginBottom:8 },
  bullet:       { width:6, height:6, borderRadius:3, backgroundColor:'#0e4e96', marginTop:7 },
  bulletText:   { flex:1, fontSize:14, color:'#475569', lineHeight:22 },
  benefitsGrid: { flexDirection:'row', flexWrap:'wrap', gap:10 },
  benefitItem:  { flexDirection:'row', alignItems:'center', gap:6, width:'48%' },
  benefitText:  { fontSize:13, color:'#475569', flex:1 },
  detailCTA:    { flexDirection:'row', gap:10, padding:16, borderTopWidth:1, borderTopColor:'#E2E8F0', backgroundColor:'#FFF' },
  detailSaveBtn:{ width:46, height:46, borderRadius:12, borderWidth:1, borderColor:'#E2E8F0', justifyContent:'center', alignItems:'center' },
  detailApplyBtn:{ flex:1, backgroundColor:'#0e4e96', borderRadius:12, justifyContent:'center', alignItems:'center', height:46 },
  detailApplyBtnApplied:{ backgroundColor:'#10B981' },
  detailApplyText:{ color:'#FFF', fontSize:15, fontWeight:'700' },
  // Filter modal
  filterSection:{ fontSize:13, fontWeight:'700', color:'#374151', marginBottom:8, marginTop:4 },
  filterGrid:   { flexDirection:'row', flexWrap:'wrap', gap:8, marginBottom:8 },
  optChip:      { borderRadius:20, borderWidth:1, borderColor:'#E2E8F0', paddingHorizontal:13, paddingVertical:7, backgroundColor:'#F8FAFC' },
  optChipActive:{ backgroundColor:'#0e4e96', borderColor:'#0e4e96' },
  optChipText:  { fontSize:12, color:'#374151', fontWeight:'500' },
  optChipTextActive:{ color:'#FFF' },
  toggleRow:    { flexDirection:'row', alignItems:'center', paddingVertical:12, borderTopWidth:1, borderTopColor:'#F1F5F9', marginTop:8 },
  modalFooter:  { flexDirection:'row', gap:12, padding:16, borderTopWidth:1, borderTopColor:'#E2E8F0' },
  footerResetBtn:{ flex:1, borderRadius:12, borderWidth:1, borderColor:'#E2E8F0', paddingVertical:13, alignItems:'center' },
  footerResetText:{ fontSize:14, color:'#64748b', fontWeight:'600' },
  footerApplyBtn:{ flex:2, backgroundColor:'#0e4e96', borderRadius:12, paddingVertical:13, alignItems:'center', justifyContent:'center', flexDirection:'row' },
  footerApplyText:{ fontSize:14, color:'#FFF', fontWeight:'700' },
  // Compare
  compareHeader:{ flexDirection:'row', padding:16, borderBottomWidth:1, borderBottomColor:'#E2E8F0', gap:10 },
  compareJobHeader:{ flex:1, alignItems:'center', gap:4 },
  compareJobTitle:{ fontSize:13, fontWeight:'700', color:'#1e293b', textAlign:'center' },
  compareJobCo:{ fontSize:11, color:'#64748b', textAlign:'center' },
  compareRow:   { flexDirection:'row', alignItems:'center', paddingVertical:10, paddingHorizontal:12 },
  compareCell:  { flex:1, fontSize:13, fontWeight:'600', textAlign:'center' },
  compareLabel: { width:80, fontSize:11, color:'#94a3b8', fontWeight:'600', textAlign:'center' },
});
